import { Resource } from "@q-food/q-food";
import { useFormSchema } from "./useFormSchema";
import { useFormSubmit } from "./useFormSubmit";

// header-case to pascalCase
function toPascalCase(str: string) {
	return str
		.split("-")
		.map((part) => part.charAt(0).toUpperCase() + part.slice(1))
		.join("");
}

/**
 * What the router hands a form **view**.
 *
 * `formName` and `resourceKey` are optional because the router does not supply them: a view route's
 * props are built from the path params plus the query (see VueRouter.ts), and neither is ever in
 * there. Declaring them as required made Vue log "Missing required prop" for every such view, while
 * FormPage derives the form name itself (`props.formName ?? getFormName(...)`) and the views name
 * their own resource.
 *
 * A form **component** has a different contract — see [FormComponentProps].
 */
export type FormProps = {
	formName?: string;
	resourceId?: string;
	resourceKey?: string;
	resourceUrl: string;
	isClone?: boolean;
};

/**
 * What a form **component** is handed by the view that renders it.
 *
 * Not the same contract as [FormProps], which is why this exists rather than the component widening
 * the other type inline: the router cannot supply `resourceKey`, but the view names its own resource
 * and passes it on, so a component that switches behaviour on it (template vs. regular, which entity
 * to translate) can rely on it being there.
 */
export type FormComponentProps = FormProps & { resourceKey: string };

const getFormName = (key: string, type: "Edit" | "Create") => {
	key = toPascalCase(key);

	return `${type}${key}Form`;
};

/**
 * Utility function for forms with a given resource
 */
function useForm<T>(formName: string, resource: Resource, resourceId?: string, model?: Ref<Record<string, any>>) {
	const { formArrayDefinition, formObjectDefinition, originalFormValues, formLayout } = useFormSchema<T>(
		formName,
		resourceId,
	);

	const { submitForm, onSubmitError, onSubmitSuccess } = useFormSubmit<T>(resource, resourceId);

	const isReady = ref(false);

	watch(
		originalFormValues,
		(values: Record<string, any>) => {
			if (Object.keys(values).includes("filesToDelete")) {
				values["filePreviewMode"] = "list";
			}

			model.value = { ...values };
			isReady.value = true;
		},
		{ once: true },
	);

	return {
		isReady,
		submitForm,
		onSubmitError,
		onSubmitSuccess,
		formArrayDefinition,
		formObjectDefinition,
		originalFormValues,
		formLayout,
	};
}

export { getFormName, useForm };
